// STIMULUS CONTROLLER: filter_assessment_modal_controller.js
import { Controller } from "@hotwired/stimulus"
export default class extends Controller {
  static targets = [
    "modal", "filterForm", "dateField", "assessmentList", "searchField","tableContent",
    "hiddenPurposes","hiddenDisciplines", "selectedFiltersContainer","assessmentHeader",
    "checkbox", "selectedIdsField", "assessmentMeta", "availableCount", "reportForm",
  ]

  static values = {
    sortColumn: String,
    sortDirection: String,
    autoOpen: Boolean
  }

  connect() {
    this.initializeDatePickers()
    this.page = 1
    this.loading = false
    this.endOfResults = false
    this.selectedIds = new Set()
    this.tableContentTarget.addEventListener("scroll", () => this.onScroll())
    this.searchFieldTarget.addEventListener("input", this.debounce(() => this.onSearch(), 400))
    this.updateSelectedFiltersDisplay()
    this.updateSelectedIds()
    this.handleEscape = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.handleEscape)

    if (this.hasAutoOpenValue && this.autoOpenValue) {
      this.pollForButtonAndOpen();
    }

    // Remove show_existing_calibrate_assessment_modal param and update the URL on form submit or NO THANKS
    this.setupModalParamCleanup();
  }

  // Utility to remove the show_existing_calibrate_assessment_modal param and update the URL
  removeShowExistingCalibrateAssessmentModalParam() {
    const url = new URL(window.location.href);
    url.searchParams.delete("show_existing_calibrate_assessment_modal");
    window.history.replaceState({}, document.title, url.toString());
  }

  setupModalParamCleanup() {
    // Use requestAnimationFrame for DOM readiness
    const setupHandlers = () => {
      const form = this.hasReportFormTarget ? this.reportFormTarget : this.element.querySelector("form");
      const noThanksBtn = document.getElementById("no-thanks-btn");
      const cleanup = () =>
        this.removeShowExistingCalibrateAssessmentModalParam();
      if (form && !form._cleanupHandlerAdded) {
        form.addEventListener("submit", () => {
          setTimeout(cleanup, 100);
        });
        form._cleanupHandlerAdded = true;
      }
      if (noThanksBtn && !noThanksBtn._cleanupHandlerAdded) {
        noThanksBtn.addEventListener("click", cleanup);
        noThanksBtn._cleanupHandlerAdded = true;
      }
      // If not found, try again next frame (max 20 tries)
      if (
        (!form || !noThanksBtn) &&
        (this._cleanupTries = (this._cleanupTries || 0) + 1) < 20
      ) {
        requestAnimationFrame(setupHandlers);
      }
    };
    setupHandlers();
  }

  pollForButtonAndOpen() {
    let tries = 0;
    const maxTries = 40;
    const pollInterval = 100;
    const poll = () => {
      const btn = document.querySelector(".addCalibrateAssessmentBtn");
      if (btn) {
        btn.click();
      } else if (tries < maxTries) {
        tries++;
        setTimeout(poll, pollInterval);
      }
    };
    poll();
  }

  loadModalData() {
    this.page = 1;
    this.endOfResults = false;
    this.assessmentListTarget.innerHTML = "";
    this.initializeDatePickers();
    this.fetchFilteredAssessments();
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape);
  }

  revealModal(modal) {
    if (!modal) return

    modal.classList.remove("hidden")
    modal.classList.add("show")
    modal.setAttribute("aria-hidden", "false")
    if (typeof bootstrap !== "undefined" && bootstrap.Modal) {
      bootstrap.Modal.getOrCreateInstance(modal).show()
    }
    modal.querySelector("button, [href], input, select, textarea")?.focus()
  }

  concealModal(modal) {
    if (!modal || modal.dataset.courseDetailsModal !== "true") return

    modal.classList.add("hidden")
    modal.classList.remove("show")
    modal.setAttribute("aria-hidden", "true")
  }

  concealCourseDetailsConfirm(event) {
    const modal = document.getElementById("confirmLinkModal")
    if (modal?.dataset.courseDetailsModal !== "true") return

    if (event) event.preventDefault()
    this.concealModal(modal)
  }

  open(event) {
    if (event) event.preventDefault();
    this.loadModalData();
    if (this.hasModalTarget && this.modalTarget.dataset.courseDetailsModal === "true") {
      this.revealModal(this.modalTarget)
    }
  }

  close(event) {
    if (event) event.preventDefault();
    this.clearFilters();
    this.removeShowExistingCalibrateAssessmentModalParam();
    if (this.hasModalTarget) this.concealModal(this.modalTarget)
  }

  clearFilters(event) {
    if (event) event.preventDefault();
    this.filterFormTarget.reset()
    this.initializeDatePickers()
    this.page = 1
    this.endOfResults = false
    this.clearSelectedFilters()
    this.assessmentListTarget.innerHTML = ""
    this.selectedIdsFieldTarget.value = []
    this.selectedIds = new Set()
    this.fetchFilteredAssessments()
  }

  applyFilter(event) {
    this.page = 1
    this.endOfResults = false
    this.assessmentListTarget.innerHTML = ""
    this.selectedIdsFieldTarget.value = []
    this.storeSelectedFilters()
    this.selectedIds = new Set()
    this.fetchFilteredAssessments()
  }

  onScroll() {
    if (this.loading || this.endOfResults) return
    const container = this.tableContentTarget
    const nearBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50
    if (nearBottom) {
      this.page++
      this.fetchFilteredAssessments()
    }
  }

  onSearch() {
    this.page = 1
    this.endOfResults = false
    this.assessmentListTarget.innerHTML = ""
    this.fetchFilteredAssessments()
  }

  fetchFilteredAssessments() {
    this.loading = true;

    const formData = this.buildFormData();
    const queryString = new URLSearchParams(formData).toString();

    fetch(`/report_dashboard/filter_assessments?${queryString}`, {
      headers: { "Accept": "text/html" }
    })
      .then(response => response.text())
      .then(html => this.handleAssessmentResponse(html))
      .catch(() => { this.loading = false });
  }

  buildFormData() {
    const formData = new FormData(this.filterFormTarget);
    formData.append("page", this.page);

    if (this.hasSortColumnValue && this.sortColumnValue) {
      formData.append("sort_column", this.sortColumnValue);
    }

    if (this.hasSortDirectionValue && this.sortDirectionValue) {
      formData.append("sort_direction", this.sortDirectionValue);
    }

    // Ensure magic link params are always included
    ["saved_report_id", "group_id", "token"].forEach(param => {
      if (!formData.has(param)) {
        const value = this.getParamFromFormOrUrl(param);
        if (value) formData.append(param, value);
      }
    });

    return formData;
  }

  getParamFromFormOrUrl(param) {
    // Try to get from a hidden input in the form
    const input = this.filterFormTarget.querySelector(`[name='${param}']`);
    if (input && input.value) return input.value;
    // Fallback: try to get from URL
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  handleAssessmentResponse(html) {
    const trimmed = html.trim();
    const hasAssessments = trimmed.includes("data-assessment-id");

    if (trimmed === "") {
      this.endOfResults = true;
    } else if (hasAssessments) {
      this.insertAssessmentsFromHtml(trimmed);
    } else {
      this.assessmentListTarget.insertAdjacentHTML("beforeend", html);
      this.assessmentHeaderTarget.classList.add("d-none");
      this.availableCountTarget.textContent = `0 Available`;
    }

    this.loading = false;
  }

  insertAssessmentsFromHtml(html) {
    const tempContainer = document.createElement("div");
    tempContainer.innerHTML = html;

    const assessmentsHtml = tempContainer.querySelector("#assessments-table")?.innerHTML || html;
    this.assessmentListTarget.insertAdjacentHTML("beforeend", assessmentsHtml);

    const meta = tempContainer.querySelector("#filtered-assessment-data");
    if (meta) {
      this.allAssessmentIds = meta.dataset.assessmentIds.split(',').map(Number);
      this.totalCount = this.allAssessmentIds.length;

      if (this.hasAvailableCountTarget) {
        this.availableCountTarget.textContent = `${this.totalCount} Available`;
      }
    }

    this.assessmentHeaderTarget.classList.remove("d-none");

    this.checkboxTargets.forEach(cb => {
      const id = parseInt(cb.closest('tr').dataset.assessmentId);
      cb.checked = this.selectedIds.has(id);
    });
  }

  initializeDatePickers() {
    if (typeof $ === "undefined" || !$.fn || !$.fn.datepicker) return

    this.dateFieldTargets.forEach((dateField) => {
      $(dateField)
        .datepicker({
          numberOfMonths: 1,
          dateFormat: "M d, yy",
          defaultDate: null,
          onSelect: () => this.applyFilter()
        })
        .on("keypress", e => e.preventDefault());

      dateField.readOnly = true;
    });
  }

  storeSelectedFilters() {
    const selectedDisciplines = Array.from(this.filterFormTarget.querySelectorAll("input[name='discipline[]']:checked"))
      .map(input => input.value)
    const selectedPurposes = Array.from(this.filterFormTarget.querySelectorAll("input[name='purpose[]']:checked"))
      .map(input => input.value)

    this.filterFormTarget.querySelector("input[name='selected_disciplines']").value = selectedDisciplines.join(',')
    this.filterFormTarget.querySelector("input[name='selected_purposes']").value = selectedPurposes.join(',')

    this.updateSelectedFiltersDisplay()
  }

  clearSelectedFilters() {
    this.filterFormTarget.querySelector("input[name='selected_disciplines']").value = ""
    this.filterFormTarget.querySelector("input[name='selected_purposes']").value = ""
    this.updateSelectedFiltersDisplay()
  }

  updateSelectedFiltersDisplay() {
    const container = this.selectedFiltersContainerTarget
    container.innerHTML = ""

    const disciplines = this.filterFormTarget.querySelector("input[name='selected_disciplines']").value.split(',').filter(Boolean)
    const purposes = this.filterFormTarget.querySelector("input[name='selected_purposes']").value.split(',').filter(Boolean)
    const titleize = str => str.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())

    const filters = [
      ...disciplines.map(v => ({ type: 'discipline', value: v })),
      ...purposes.map(v => ({ type: 'purpose', value: v }))
    ]

    filters.forEach(filter => {
      const tag = document.createElement("span")
      tag.className = "filter-easy-clear-button me-2"
      tag.innerHTML = `${titleize(filter.value)} <a href="#" class="text-black ms-1" data-action="click->filter-assessment-modal#removeFilter" data-filter-type="${filter.type}" data-filter-value="${filter.value}">&times;</a>`
      container.appendChild(tag)
    })
  }

  removeFilter(event) {
    event.preventDefault()
    const type = event.target.dataset.filterType
    const value = event.target.dataset.filterValue
    const inputSelector = `input[name='${type}[]'][value='${value}']`
    const input = this.filterFormTarget.querySelector(inputSelector)
    if (input) input.checked = false
    this.applyFilter(event)
  }

  debounce(func, wait) {
    let timeout
    return function(...args) {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }

  selectAllShown() {
    if (!this.allAssessmentIds) return

    // Update hidden input via Stimulus target
    if (this.hasSelectedIdsFieldTarget) {
      this.selectedIds = new Set(this.allAssessmentIds)
      this.selectedIdsFieldTarget.value = this.allAssessmentIds.join(',')
    }

    // Check all visible checkboxes
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = true
    })
  }

  sort(event) {
    const clickedColumn = event.target.dataset.sortColumn;
    const currentDirection = this.sortDirectionValue;
    const currentColumn = this.sortColumnValue;

    if (currentColumn === clickedColumn) {
      this.sortDirectionValue = currentDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortDirectionValue = event.target.dataset.sortOrder || "asc";
    }

    this.sortColumnValue = clickedColumn;
    this.applyFilter();
  }

  updateSelectedIds() {
    this.checkboxTargets.forEach(cb => {
      const id = parseInt(cb.closest('tr').dataset.assessmentId)
      if (cb.checked) {
        this.selectedIds.add(id)
      } else if (this.hasReportFormTarget && this.reportFormTarget.dataset.assessmentIds.includes(id.toString())) {
        cb.checked = true
        this.selectedIds.add(id)
      } else {
        this.selectedIds.delete(id)
      }
    })
    const selectedIds = [...this.selectedIds]
    this.selectedIdsFieldTarget.value = selectedIds.join(',')
    const selectedIdForRelation = document.getElementById('selected_assessments_for_relation')
    if(selectedIdForRelation){
      selectedIdForRelation.value = selectedIds.join(',')
    }
    if (this.hasReportFormTarget) {
      for (const assessmentId of this.selectedIds) {
        const exists = this.reportFormTarget.querySelector(
          `input[name="selected_assessment_ids[]"][value="${assessmentId}"]`
        );
        if (!exists) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selected_assessment_ids[]';
          input.value = assessmentId;
          this.reportFormTarget.appendChild(input);
        }
      }
    }
    this.toggleCalibrateButtonVisibility()
  }

  redirectToSelectedResults(event) {
    const ids = this.selectedIdsFieldTarget.value.split(',').filter(Boolean)
    if (ids.length === 0) {
      alert("Please select at least one assessment.")
      return
    }

    this.navigateToReports(ids)
  }

  navigateToReports(ids) {
    const url = ids.length === 1
      ? `/assessment/reports/educator/cohort_progresses/${ids[0]}`
      : `/assessment/reports/educator/cohort_progresses?${ids.map(id => `ids[]=${id}`).join('&')}`

    window.location.href = url
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.close();
    }
  }

  toggleCalibrateButtonVisibility() {
    const hiddenFields = document.querySelectorAll("input[name='selected_assessment_ids[]']");
    const button = document.getElementById('assessment-filter-button');
    const performancePills = document.querySelectorAll("#filterPills a[data-pill-type='performance']");

    if (hiddenFields && hiddenFields.length > 0) {
      button?.classList?.remove("d-none");
    } else {
      button?.classList?.add("d-none");
      const element = document.querySelector("[data-controller~='report-filter']");
      if (!element) return;
      let reportFilterController;
      try {
        reportFilterController = this.application.getControllerForElementAndIdentifier(element, "report-filter");
      } catch {
        return;
      }

      if (reportFilterController && performancePills.length > 0) {
        performancePills.forEach((pill) => {
          const id = pill.dataset.pillId;
          reportFilterController.removePill(id, "performance");
          reportFilterController.selectedPerformance =
            reportFilterController.selectedPerformance.filter((pid) => pid !== id);
        });

        reportFilterController.selectedCalibratePerformanceTarget.value =
          reportFilterController.selectedPerformance.join(",");
        reportFilterController.submitFilters();
        reportFilterController.toggleClearFiltersButton();
      }
    }
  }

  openConfirmModal(event) {
    const selectedIds = Array.from(this.selectedIds || []);
    const reportNameField = this.element.querySelector("input[name='report_name']");
    if (!reportNameField || reportNameField.value !== "course_report") {
      return;
    }

    // Build confirmation list
    const listEl = document.getElementById("confirm-assessment-list");
    if (listEl) {
      listEl.innerHTML = "";
      selectedIds.forEach(id => {
        const row = this.checkboxTargets.find(
          cb => parseInt(cb.closest("tr").dataset.assessmentId, 10) === id
        )?.closest("tr");

        if (row) {
          try {
            const info = JSON.parse(row.dataset.assessmentInfo || "{}");
            const li = document.createElement("li");
            li.textContent = `${info.nickname || ""} - ${info.exam_course || ""} - ${info.purpose || ""} - ${info.start_date || ""} - ${info.end_date || ""}`;
            listEl.appendChild(li);
          } catch (e) {
            console.error("Invalid JSON in data-assessment-info", e);
          }
        }
      });
    }

    // Populate hidden field for form submission
    const selectedInput = document.getElementById("selected_assessments_for_relation");
    if (selectedInput) selectedInput.value = selectedIds.join(",");

    const modal = this.modalTarget;
    modal.classList.remove('show');
    if (modal.dataset.courseDetailsModal === "true") {
      modal.classList.add("hidden");
      modal.setAttribute("aria-hidden", "true");
    }

    // Close selection modal via its dismiss button, then open confirm modal
    const openConfirmTrigger = document.getElementById("openConfirmModalBtn");
    if (openConfirmTrigger) openConfirmTrigger.click();
    const confirmModal = document.getElementById("confirmLinkModal");
    if (confirmModal?.dataset.courseDetailsModal === "true") this.revealModal(confirmModal);
  }
}
